/**
 * Shared tree-traversal helpers for the Architectural Hierarchy creation tree.
 * Extracted from Structure.jsx so IssueScreenPanel can reuse them.
 */
import { migrateFiltersFromLegacy } from '@modules/folders/frontend/utils/edgeFilters';

/**
 * Get valid parent type names for a given child issue type from the tree.
 * @param {string} childTypeName - The issue type name of the child
 * @param {Array} treeData - The creation tree nodes
 * @returns {string[]} Array of valid parent issue type names
 */
export function getValidParentTypes(childTypeName, treeData) {
  if (!treeData || !childTypeName) return [];
  const parentTypes = [];
  const search = (nodes) => {
    for (const node of nodes) {
      if (node.children?.some(c => c.issueTypeName === childTypeName)) {
        if (!parentTypes.includes(node.issueTypeName)) {
          parentTypes.push(node.issueTypeName);
        }
      }
      search(node.children || []);
    }
  };
  search(treeData);
  return parentTypes;
}

/**
 * Get relationship info for a child type under any parent in the tree.
 * @param {string} childTypeName - The issue type name of the child
 * @param {Array} treeData - The creation tree nodes
 * @param {string} defaultLinkTypeName - Fallback link type name
 * @returns {{ relationshipType: string, linkTypeName: string, linkTypeId: string, linkDirection: string } | null}
 */
/**
 * Get allowed child types for a given parent issue type from the tree.
 * @param {string} parentTypeName - The issue type name of the parent
 * @param {Array} treeData - The creation tree nodes
 * @param {string} defaultLinkTypeName - Fallback link type name
 * @returns {Array<{ issueTypeName: string, relationshipType: string, linkTypeName: string, linkDirection: string }>}
 */
export function getAllowedChildTypes(parentTypeName, treeData, defaultLinkTypeName) {
  if (!treeData || !parentTypeName) return [];
  const results = [];
  const search = (nodes) => {
    for (const node of nodes) {
      if (node.issueTypeName === parentTypeName && node.children?.length > 0) {
        for (const child of node.children) {
          if (!results.find(r => r.issueTypeName === child.issueTypeName)) {
            results.push({
              issueTypeId: child.issueTypeId != null ? String(child.issueTypeId) : '',
              issueTypeName: child.issueTypeName,
              iconUrl: child.iconUrl || '',
              subtask: !!child.subtask,
              relationshipType: child.relationshipType || (child.subtask ? 'subtask' : 'linked'),
              linkTypeName: child.linkTypeName || defaultLinkTypeName,
              linkTypeId: child.linkTypeId ? String(child.linkTypeId) : '',
              linkDirection: child.linkDirection || 'outward',
              filters: migrateFiltersFromLegacy(child),
            });
          }
        }
      }
      search(node.children || []);
    }
  };
  search(treeData);
  return results;
}

/**
 * Get relationship info for a child type under any parent in the tree.
 * @param {string} childTypeName - The issue type name of the child
 * @param {Array} treeData - The creation tree nodes
 * @param {string} defaultLinkTypeName - Fallback link type name
 * @returns {{ relationshipType: string, linkTypeName: string, linkTypeId: string, linkDirection: string } | null}
 */
/**
 * Reverse of getAllowedChildTypes — returns every relationship in the
 * tree where `childTypeName` appears as a child, with full edge info
 * (filters, link type, direction). The unified "+ Add link" picker in
 * the side panel uses this to offer "as my parent" options alongside
 * "as my child" options from getAllowedChildTypes.
 *
 * Each result is an edge from candidate (parent side) → current item:
 *   - parentTypeName: the type the picker should search for
 *   - relationshipType / linkTypeName / linkDirection: how to wire the link
 *   - filters: per-edge JQL + expression filters
 *
 * @returns {Array<{ parentTypeName, relationshipType, linkTypeName, linkDirection, filters }>}
 */
export function getAllowedParentRelationships(childTypeName, treeData, defaultLinkTypeName) {
  if (!treeData || !childTypeName) return [];
  const results = [];
  const seen = new Set();
  const walk = (nodes) => {
    for (const node of nodes) {
      for (const child of (node.children || [])) {
        if (child.issueTypeName === childTypeName) {
          const relationshipType = child.relationshipType || (child.subtask ? 'subtask' : 'linked');
          const linkTypeName = child.linkTypeName || defaultLinkTypeName;
          const linkDirection = child.linkDirection || 'outward';
          const key = `${node.issueTypeName}|${relationshipType}|${linkTypeName}|${linkDirection}`;
          if (!seen.has(key)) {
            seen.add(key);
            results.push({
              parentTypeName: node.issueTypeName,
              relationshipType,
              linkTypeName,
              linkDirection,
              filters: migrateFiltersFromLegacy(child),
            });
          }
        }
      }
      walk(node.children || []);
    }
  };
  walk(treeData);
  return results;
}

/**
 * Collect every unique (relationshipType + linkTypeName + linkDirection)
 * edge across the entire creation tree. Used by the descendant-walk
 * endpoint to know which kinds of relationships count as "child" links
 * (and therefore which to follow when computing the exclusion set).
 *
 * @returns {Array<{ relationshipType, linkTypeName, linkDirection }>}
 */
export function extractAllEdgesFromTree(treeData) {
  if (!treeData) return [];
  const edges = [];
  const seen = new Set();
  const walk = (nodes) => {
    for (const node of nodes) {
      for (const child of (node.children || [])) {
        const edge = {
          relationshipType: child.relationshipType || (child.subtask ? 'subtask' : 'linked'),
          linkTypeName: child.linkTypeName || '',
          linkDirection: child.linkDirection || 'outward',
        };
        const key = `${edge.relationshipType}|${edge.linkTypeName}|${edge.linkDirection}`;
        if (!seen.has(key)) {
          seen.add(key);
          edges.push(edge);
        }
      }
      walk(node.children || []);
    }
  };
  walk(treeData);
  return edges;
}

/**
 * The options behind the item view's "+ Create" button: every child type the
 * Architectural Hierarchy allows under `parentTypeName`, narrowed to the types
 * the PROJECT can actually create (the tree is per work type scheme; a scheme
 * edit can leave a tree node pointing at a type the project no longer has).
 *
 * Matches a tree node to a project type by id first, then by name — trees
 * saved before ids were stored carry only the name, and a rename in Jira is
 * survived by the id. The project type wins for id/name/iconUrl (it is the
 * live record); the tree node wins for the edge (how the child attaches).
 *
 * @param {string} parentTypeName - The open item's work type name
 * @param {Array} treeData - The creation tree nodes
 * @param {Array<{ id, name, iconUrl, subtask }>} projectIssueTypes - GET project issue types
 * @param {string} defaultLinkTypeName - Fallback link type name
 * @returns {Array<{ issueTypeId, issueTypeName, iconUrl, edge }>}
 */
export function resolveHierarchyCreateOptions(parentTypeName, treeData, projectIssueTypes, defaultLinkTypeName) {
  const allowed = getAllowedChildTypes(parentTypeName, treeData, defaultLinkTypeName);
  if (allowed.length === 0) return [];
  const types = Array.isArray(projectIssueTypes) ? projectIssueTypes : [];
  const byId = new Map(types.map(t => [String(t.id), t]));
  const byName = new Map(types.map(t => [String(t.name || '').toLowerCase(), t]));
  const out = [];
  const seen = new Set();
  for (const edge of allowed) {
    const type = (edge.issueTypeId && byId.get(edge.issueTypeId))
      || byName.get(String(edge.issueTypeName || '').toLowerCase());
    if (!type || seen.has(String(type.id))) continue;
    seen.add(String(type.id));
    out.push({
      issueTypeId: String(type.id),
      issueTypeName: type.name,
      iconUrl: type.iconUrl || edge.iconUrl || '',
      edge,
    });
  }
  return out;
}

const relationshipOf = (child, defaultLinkTypeName) => ({
  relationshipType: child.relationshipType || (child.subtask ? 'subtask' : 'linked'),
  linkTypeName: child.linkTypeName || defaultLinkTypeName,
  linkTypeId: child.linkTypeId || '',
  linkDirection: child.linkDirection || 'outward',
  filters: migrateFiltersFromLegacy(child),
});

/**
 * How a child type hangs under its parent. A tree may hang one type two ways
 * (an Engineering under the Epic by the parent column, an Engineering section
 * under another Engineering by a link), so when the parent's type is known
 * that pair's edge answers; otherwise, or when the tree has no such pair, the
 * first match wins. Twin of hierarchy-rules.js relationshipForChild.
 * @param {string} [parentTypeName] - The parent's issue type name, when known
 */
export function getRelationshipForChild(childTypeName, treeData, defaultLinkTypeName, parentTypeName = null) {
  if (!treeData || !childTypeName) return null;
  let result = null;
  const search = (nodes, underParent) => {
    for (const node of nodes) {
      if (result) return;
      if (!underParent || node.issueTypeName === parentTypeName) {
        const child = node.children?.find(c => c.issueTypeName === childTypeName);
        if (child) { result = relationshipOf(child, defaultLinkTypeName); return; }
      }
      search(node.children || [], underParent);
    }
  };
  if (parentTypeName) search(treeData, true);
  if (!result) search(treeData, false);
  return result;
}

/**
 * Every distinct way the tree hangs a child type, in tree order (the first is
 * getRelationshipForChild's answer). Twin of hierarchy-rules.js
 * relationshipsForChild.
 */
export function getRelationshipsForChild(childTypeName, treeData, defaultLinkTypeName) {
  if (!treeData || !childTypeName) return [];
  const out = [];
  const seen = new Set();
  const walk = (nodes) => {
    for (const node of nodes) {
      for (const child of (node.children || [])) {
        if (child.issueTypeName !== childTypeName) continue;
        const rel = relationshipOf(child, defaultLinkTypeName);
        const id = `${rel.relationshipType}|${String(rel.linkTypeName).toLowerCase()}|${rel.linkDirection}`;
        if (!seen.has(id)) { seen.add(id); out.push(rel); }
      }
      walk(node.children || []);
    }
  };
  walk(treeData);
  return out;
}
